import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQItemProps {
  question: string;
  answer: string;
}

const FAQItem = ({ question, answer }: FAQItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-white/10 last:border-0">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-6 flex items-center justify-between text-left hover:bg-white/5 transition-colors px-4 rounded-lg group"
      >
        <span className="font-semibold text-lg md:text-xl lg:text-2xl pr-8 text-white group-hover:text-accent transition-colors">{question}</span>
        <div className={`p-1 rounded-full border-2 border-white/20 transition-all duration-300 flex-shrink-0 ${isOpen ? 'bg-pink border-pink text-white rotate-180' : 'bg-transparent text-white'}`}>
          {isOpen ? <X size={20} /> : <Plus size={20} />}
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="pb-8 pt-2 px-4 text-base md:text-lg text-muted leading-relaxed max-w-3xl">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQ = () => {
  const faqs = [
    {
      question: "Do you really guarantee views?",
      answer: "Yes. We are so confident in our strategies that we offer concrete view guarantees. If we don't hit the agreed-upon targets for your content, we continue posting until you do or you get your money back. We take the risk, so you don't have to."
    },
    {
      question: "Which games do you specialize in?",
      answer: "We specialize in the ecosystems of Minecraft, Roblox, Fortnite, Hytale, and Hytopia. We understand the specific culture, trends, and player psychology of these communities. We're also starting with mobile games, discord games, and web games. Reach out to see if we can help you with your game."
    },
    {
      question: "Do I need to record the footage?",
      answer: "We prefer to capture footage ourselves directly from your game to ensure the highest quality and best storytelling. However, if you have specific clips, we can work with those too."
    },
    {
      question: "What is included in 'Full Account Management'?",
      answer: "We handle everything: scheduling, posting, writing captions, researching hashtags, and engaging with comments. We act as your internal social media team."
    },
    {
      question: "How fast will I see results?",
      answer: "Viral growth can happen overnight, but consistent community building takes time. Most clients see significant traffic increases within the first 30 days of our consistent posting schedule."
    },
    {
      question: "Why $1,900/month?",
      answer: "We provide a premium, full-service solution with a dedicated team of editors, strategists, and managers. Compared to hiring a single full-time employee, Gamefizz gives you an entire department for a fraction of the cost."
    },
    {
      question: "Why wouldn't I just hire a full-time social media manager?",
      answer: "Finding a specialized social media manager with years of experience can prove to be difficult and hiring one will cost on average $60,000 - $90,000; not to mention impractical if you don't have enough work to require a full-time manager."
    },
    {
      question: "How fast can you start making content?",
      answer: "We typically have a 1 week setup phase which gives us time to setup and warm your account(s), explore your experience to start brainstorming content, and plan out."
    },
    {
      question: "What if I don't like the content?",
      answer: "Our priority is to make viral content that can get views and convert to players. If you don't like the content that's being produced, we have a content management system on Notion that you'll have access to where feedback and suggestions can be provided."
    },
    {
      question: "What do I need to do?",
      answer: "During the setup phase we will ask you to provide basic assets and permissions like access to admin perms and any brand assets you have like logos, fonts, colors etc. We might ask to setup a certain code throughout the campaign to reward fans, but besides that it's a very hands-off process."
    }
  ];

  return (
    <section className="py-16 md:py-24 lg:py-32 px-4 md:px-8 bg-dark">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-block">
            <h2 className="font-semibold text-3xl sm:text-4xl md:text-5xl text-white">FAQ</h2>
            <svg className="w-full h-3 mt-1 text-pink" viewBox="0 0 100 12" fill="none">
              <motion.path 
                d="M2 6 Q 15 2, 30 6 T 55 6 T 80 6 T 98 6"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
            </svg>
          </div>
        </div>
        
        <div className="flex flex-col gap-2">
          {faqs.map((faq, index) => (
            <FAQItem key={index} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;